import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, MessageCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingUI from "../components/FloatingUI";
import { Reveal, SectionTag } from "../components/motion";
import { wa, PASSEIOS_DETALHE } from "../lib/site";

export default function PasseioDetalhe() {
  const { slug } = useParams();
  const p = PASSEIOS_DETALHE.find((x) => x.slug === slug);
  const outros = PASSEIOS_DETALHE.filter((x) => x.slug !== slug).slice(0, 3);

  useEffect(() => {
    if (p) {
      document.title = p.seoTitle;
      document.querySelector('meta[name="description"]')?.setAttribute("content", p.seoDesc);
      window.scrollTo(0, 0);
    }
  }, [p]);

  if (!p) return <Navigate to="/passeios" replace />;

  return (
    <>
      <Header />
      <main>
        <section className="relative overflow-hidden bg-brand-deep grain pt-52 pb-0" data-testid="passeio-hero">
          <div className="absolute inset-0">
            <img src={p.img} alt={p.title} className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-deep/70 to-brand-deep" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-16">
            <Reveal>
              <Link
                to="/passeios"
                data-testid="passeio-voltar"
                className="inline-flex items-center gap-2 text-white/70 text-sm font-semibold transition-colors hover:text-accent-soft"
              >
                <ArrowLeft size={15} />
                Todos os passeios
              </Link>
              <p className="mt-8 text-accent-soft text-xs font-bold uppercase tracking-[0.3em]">{p.flag} Passeio {p.num}</p>
              <h1 className="mt-4 max-w-3xl font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-balance">
                {p.title}
              </h1>
              <p className="mt-6 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed">{p.intro}</p>
              <a
                href={wa(`Olá, Magnus! ${p.cta}. Pode me passar detalhes e disponibilidade?`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="passeio-hero-cta"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-whatsapp px-8 py-4 text-sm font-bold text-white shadow-xl shadow-whatsapp/30 transition-transform duration-300 hover:scale-[1.04]"
              >
                <MessageCircle size={18} />
                {p.cta}
              </a>
            </Reveal>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-sand" data-testid="passeio-conteudo">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-12">
            <Reveal className="lg:col-span-3">
              <SectionTag>O que você vai viver</SectionTag>
              <ul className="mt-8 space-y-5" data-testid="passeio-experiencias">
                {p.experiencias.map((e) => (
                  <li key={e} className="flex items-start gap-4 rounded-2xl border border-brand/10 bg-white p-5">
                    <span className="grid place-items-center w-8 h-8 rounded-full bg-brand/10 text-brand shrink-0 mt-0.5">
                      <Check size={16} strokeWidth={2.5} />
                    </span>
                    <p className="text-ink text-sm md:text-base leading-relaxed">{e}</p>
                  </li>
                ))}
              </ul>
              <div className="mt-8 rounded-2xl bg-accent-soft/40 border border-accent/30 p-6" data-testid="passeio-dica">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">Dica do Magnus</p>
                <p className="mt-2 text-ink/80 text-sm md:text-base leading-relaxed">{p.dica}</p>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-2">
              <div className="lg:sticky lg:top-44 rounded-3xl bg-brand-deep text-white p-8 md:p-10 grain relative overflow-hidden" data-testid="passeio-infos">
                <p className="text-accent-soft text-xs font-bold uppercase tracking-[0.3em]">Informações práticas</p>
                <ul className="mt-6 space-y-5">
                  {p.infos.map((i) => (
                    <li key={i.label} className="border-b border-white/10 pb-5 last:border-0 last:pb-0">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-white/50">{i.label}</p>
                      <p className="mt-1 font-display font-bold text-base md:text-lg">{i.value}</p>
                    </li>
                  ))}
                </ul>
                <a
                  href={wa(`Olá, Magnus! ${p.cta}. Pode consultar disponibilidade?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="passeio-infos-cta"
                  className="mt-8 flex items-center justify-center gap-2 rounded-full bg-whatsapp px-6 py-4 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.03]"
                >
                  <MessageCircle size={17} />
                  Consultar disponibilidade
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-20 md:py-24 bg-white" data-testid="passeio-relacionados">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal className="mb-12">
              <SectionTag>Continue explorando</SectionTag>
              <h2 className="mt-5 font-display font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-deep">
                Outros passeios na <span className="font-serif italic font-medium text-brand-light">Tríplice Fronteira</span>
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {outros.map((o) => (
                <Reveal key={o.slug}>
                  <Link
                    to={`/passeios/${o.slug}`}
                    data-testid={`relacionado-${o.slug}`}
                    className="group relative block overflow-hidden rounded-3xl aspect-[4/3]"
                  >
                    <img src={o.img} alt={o.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/90 via-brand-deep/25 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <span className="text-accent-soft text-[11px] font-bold uppercase tracking-[0.25em]">{o.flag} Passeio {o.num}</span>
                      <h3 className="mt-1.5 font-display font-bold text-white text-lg leading-snug">{o.title}</h3>
                      <span className="mt-3 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.2em] text-white">
                        Ver passeio
                        <ArrowRight size={14} className="text-accent transition-transform duration-300 group-hover:translate-x-1.5" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingUI />
    </>
  );
}
